import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { InputField } from '@/components/InputField';
import { SelectField } from '@/components/SelectField';
import { ImageUpload } from '@/components/ImageUpload';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { ModalFormData, modalFormSchema } from './schemas';
import { RosterGroup, RosterMember } from '@/model';
import { RoleLabelField } from '../RoleLabelField';
import {
  usePutRosterMemberMutation,
  usePutRosterMemberStatisticsMutation,
} from '@/services/queries/roster-member';
import { PutRosterMemberRequest, RosterMemberStatisticsRequest } from '@/services/request/roster-member';
import { RosterMemberStatsFields } from '../RosterMemberStatsFields';

export interface RosterMemberEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: RosterMember;
  groups: RosterGroup[];
  onClose: () => void;
  onDone?: () => void;
}

const statFromItem = (stats: Record<string, number> | null | undefined, key: string): number =>
  (stats && typeof stats[key] === 'number' ? stats[key] : 0) as number;

export const RosterMemberEditModal = ({ isOpen, item, groups, onClose, onDone }: RosterMemberEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putItemMutation = usePutRosterMemberMutation();
  const statsItemMutation = usePutRosterMemberStatisticsMutation();

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      type: item.type,
      team_uuid: item.team_uuid ?? '',
      group_uuid: item.group_uuid ?? '',
      name: item.name,
      role_label: item.role_label ?? '',
      hyperlink: item.hyperlink ?? '',
      shirt_number: item.shirt_number ?? '',
      birthdate: item.birthdate ?? '',
      age: item.age ?? '',
      nationality: item.nationality ?? '',
      height: item.height ?? '',
      contract_until: item.contract_until ?? '',
      image: undefined,
      stats: {
        matches: statFromItem(item.stats, 'matches'),
        wins: statFromItem(item.stats, 'wins'),
        draw: statFromItem(item.stats, 'draw'),
        lose: statFromItem(item.stats, 'lose'),
        goals: statFromItem(item.stats, 'goals'),
        assists: statFromItem(item.stats, 'assists'),
        total_shots: statFromItem(item.stats, 'total_shots'),
        shots_on_target: statFromItem(item.stats, 'shots_on_target'),
        yellow_cards: statFromItem(item.stats, 'yellow_cards'),
        red_cards: statFromItem(item.stats, 'red_cards'),
        goals_conceded: statFromItem(item.stats, 'goals_conceded'),
      },
    },
    resolver: zodResolver(modalFormSchema),
    criteriaMode: 'all',
  });

  const type = watch('type');
  const isPlayer = type === 'player';
  const selectedGroupUUID = watch('group_uuid');
  const positionOrder = groups.find(g => g.uuid === selectedGroupUUID)?.position_order;

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) {
        return;
      }

      const payload: PutRosterMemberRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        group_uuid: data.group_uuid,
        type: data.type,
        name: data.name,
        role_label: data.role_label,
        hyperlink: data.hyperlink,
        ...(data.image ? { image: data.image } : {}),
        ...(data.type === 'player'
          ? {
              shirt_number: data.shirt_number,
              birthdate: data.birthdate,
              age: data.age,
              nationality: data.nationality,
              height: data.height,
              contract_until: data.contract_until,
            }
          : {}),
      };

      putItemMutation.mutate(
        { id: item.uuid, payload },
        {
          onSuccess: () => {
            if (data.type !== 'player') {
              onDone?.();
              toast({
                status: 'success',
                title: t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_success'),
              });
              return;
            }

            const statsPayload: RosterMemberStatisticsRequest = {
              matches: data.stats.matches,
              wins: data.stats.wins,
              draw: data.stats.draw,
              lose: data.stats.lose,
              goals: data.stats.goals,
              assists: data.stats.assists,
              total_shots: data.stats.total_shots,
              shots_on_target: data.stats.shots_on_target,
              yellow_cards: data.stats.yellow_cards,
              red_cards: data.stats.red_cards,
              goals_conceded: data.stats.goals_conceded,
            };

            statsItemMutation.mutate(
              { id: item.uuid, payload: statsPayload },
              {
                onSuccess: () => {
                  onDone?.();
                  toast({
                    status: 'success',
                    title: t(
                      'pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_success',
                    ),
                  });
                },
                onError: (resp: any) => {
                  toast({
                    status: 'error',
                    title:
                      (resp && resp.message) ||
                      t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_error'),
                  });
                },
              },
            );
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, statsItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_team')}
              placeholder={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_team')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>
                  {t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_type')}
                </Text>
                <SelectField
                  {...register('type')}
                  rootProps={{ isInvalid: !!errors?.type, errorMessage: errors?.type?.message, mt: 0 }}
                >
                  <option value='player'>
                    {t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_type_player')}
                  </option>
                  <option value='staff'>
                    {t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_type_staff')}
                  </option>
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>
                  {t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_group')}
                </Text>
                <SelectField
                  {...register('group_uuid')}
                  rootProps={{ isInvalid: !!errors?.group_uuid, errorMessage: errors?.group_uuid?.message, mt: 0 }}
                >
                  <option value='' disabled>
                    {t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_group_default')}
                  </option>
                  {groups.map(group => (
                    <option key={group.uuid} value={group.uuid}>
                      {group.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>
            </Flex>

            <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
              <InputField
                withoutFloating
                label={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_name')}
                maxLength={100}
                rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
                {...register('name')}
              />
              <RoleLabelField
                control={control}
                name='role_label'
                label={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_role_label')}
                placeholder={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_role_label_placeholder')}
                noOptionsMessage={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_role_label_no_options')}
                positionOrder={positionOrder}
                error={errors?.role_label?.message as string | undefined}
              />
            </Flex>

            <InputField
              withoutFloating
              label={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_hyperlink')}
              maxLength={255}
              rootProps={{ isInvalid: !!errors?.hyperlink, errorMessage: errors?.hyperlink?.message }}
              {...register('hyperlink')}
            />

            {isPlayer ? (
              <>
                <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
                  <InputField
                    withoutFloating
                    label={t(
                      'pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_shirt_number',
                    )}
                    maxLength={3}
                    rootProps={{ isInvalid: !!errors?.shirt_number, errorMessage: errors?.shirt_number?.message }}
                    {...register('shirt_number')}
                  />
                  <InputField
                    withoutFloating
                    label={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_age')}
                    maxLength={3}
                    rootProps={{ isInvalid: !!errors?.age, errorMessage: errors?.age?.message }}
                    {...register('age')}
                  />
                </Flex>

                <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
                  <InputField
                    withoutFloating
                    type='date'
                    label={t(
                      'pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_birthdate',
                    )}
                    fullDatePickerArea
                    rootProps={{ isInvalid: !!errors?.birthdate, errorMessage: errors?.birthdate?.message }}
                    {...register('birthdate')}
                  />
                  <InputField
                    withoutFloating
                    label={t(
                      'pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_nationality',
                    )}
                    rootProps={{ isInvalid: !!errors?.nationality, errorMessage: errors?.nationality?.message }}
                    {...register('nationality')}
                  />
                </Flex>

                <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
                  <InputField
                    withoutFloating
                    label={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_height')}
                    rootProps={{ isInvalid: !!errors?.height, errorMessage: errors?.height?.message }}
                    {...register('height')}
                  />
                  <InputField
                    withoutFloating
                    type='date'
                    min={formatDateToDateInputValue(new Date())}
                    label={t(
                      'pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_contract_until',
                    )}
                    fullDatePickerArea
                    rootProps={{ isInvalid: !!errors?.contract_until, errorMessage: errors?.contract_until?.message }}
                    {...register('contract_until')}
                  />
                </Flex>

                <RosterMemberStatsFields register={register} errors={errors} />
              </>
            ) : null}

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_photo')}
                imageURL={item.picture ?? ''}
                onFileChange={file => setValue('image', file)}
                helpText={t(
                  'pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_photo_formats',
                )}
                error={errors?.image?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='flex-end' gap='kilo'>
          <Flex gap={2}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_roster_member.components.roster_member_edit_modal.roster_member_edit_save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading || statsItemMutation.isLoading} />
    </Modal>
  );
};
